function statusToIota(s) {
	switch (s) {
		default:
			return 0;
		case 'processed':
			return 1;
		case 'confirmed':
			return 2;
		case 'finalized':
			return 3;
		case 'timeout':
			return 4;
	}
}

export default {
	namespaced: true,
	state: () => ({
		tradeUserChannel: new BroadcastChannel('trade:user'),
		signatureStatus: new BroadcastChannel('signature:status'),
		lastCheck: 0,
		activeSignatures: {},
		activeSignatureCount: 0,
		lastRefresh: 0,
		autoRemoveTimeouts: {},
	}),
	mutations: {
		add_signature(state, { sig, status, tokenInfo }) {
			console.log('DEBUG: signatures::add_signature', sig, status, tokenInfo);
			state.activeSignatures[sig] = {
				status: status,
				createdAt: new Date(),
				tokenInfo: tokenInfo || null,
			};
			state.activeSignatureCount++;
		},

		// Remove old transactions (older than 1 minute)
		remove_old_transactions(state) {
			const now = new Date();
			const oneMinuteAgo = new Date(now.getTime() - 60000);

			const keys = Object.keys(state.activeSignatures);
			let removedCount = 0;

			keys.forEach((sig) => {
				const createdAt = new Date(state.activeSignatures[sig].createdAt);
				if (createdAt < oneMinuteAgo) {
					delete state.activeSignatures[sig];
					removedCount++;
				}
			});

			if (removedCount > 0) {
				console.log(`Removed ${removedCount} old transactions (older than 1 minute)`);
				state.activeSignatureCount = Math.max(0, state.activeSignatureCount - removedCount);
			}
		},

		update_status(state, { sig, status, pendingSig = null, tokenInfo = null }) {
			if (!status) return;

			console.log('DEBUG: signatures::update_status', sig, { status, pendingSig, tokenInfo });
			if (
				!pendingSig &&
				statusToIota(state.activeSignatures[sig]?.status?.confirmationStatus) >
					statusToIota(status?.confirmationStatus)
			)
				return;

			if (pendingSig) {
				// Preserve token information when clearing pending signature
				const pendingTokenInfo = state.activeSignatures[pendingSig]?.tokenInfo;
				console.log(
					'DEBUG: signatures::clear_pending',
					sig,
					pendingSig,
					'pendingTokenInfo:',
					pendingTokenInfo,
					'newTokenInfo:',
					tokenInfo
				);
				delete state.activeSignatures[pendingSig];
				state.activeSignatures[sig] = Object.assign(
					state.activeSignatures[sig] || {
						createdAt: new Date(),
						status: status,
						tokenInfo: pendingTokenInfo || tokenInfo,
					}
				);
			}

			// Preserve existing token information when updating status
			// If tokenInfo is provided in the update, use that instead
			const existingTokenInfo = tokenInfo || state.activeSignatures[sig]?.tokenInfo;
			console.log('DEBUG: Using tokenInfo for signature', sig, 'tokenInfo:', existingTokenInfo);

			// Make sure we're preserving the token information structure
			state.activeSignatures[sig] = {
				...(state.activeSignatures[sig] || {}),
				...status,
				tokenInfo: existingTokenInfo,
				createdAt: state.activeSignatures[sig]?.createdAt || new Date(),
			};

			state.activeSignatureCount++;
			state.tradeUserChannel.postMessage({ signature: sig });

			// Schedule auto-removal for transactions with a result
			if (
				status?.confirmationStatus === 'confirmed' ||
				status?.confirmationStatus === 'finalized' ||
				status?.confirmationStatus === 'timeout' ||
				status?.err
			) {
				// Clear any existing timeout for this signature
				if (state.autoRemoveTimeouts[sig]) {
					clearTimeout(state.autoRemoveTimeouts[sig]);
				}

				// Set a new timeout to remove this transaction after 15 seconds
				state.autoRemoveTimeouts[sig] = setTimeout(() => {
					// Use a function to access the current state
					// We can't use the state parameter directly as it would be stale
					import('@/store').then((storeModule) => {
						const store = storeModule.default;
						if (store) {
							store.commit('signatures/clear_status', sig);
							// The deletion of the timeout will happen in the clear_status mutation
						}
					});
				}, 15000); // 15 seconds
			}
		},
		clear_status(state, sig) {
			// console.log("signatures::clear_status", sig)
			// Clear any existing timeout for this signature
			if (state.autoRemoveTimeouts[sig]) {
				clearTimeout(state.autoRemoveTimeouts[sig]);
				delete state.autoRemoveTimeouts[sig];
			}
			delete state.activeSignatures[sig];
			state.activeSignatureCount--;
		},
		clear_all(state) {
			// console.log("signatures::clear_all")
			// Clear all timeouts
			Object.keys(state.autoRemoveTimeouts).forEach((sig) => {
				clearTimeout(state.autoRemoveTimeouts[sig]);
			});
			state.autoRemoveTimeouts = {};
			state.activeSignatures = {};
			state.lastCheck = 0;
			state.activeSignatureCount = 0;
		},
	},
	actions: {
		//Race condition with the clear_all
		async refresh({ state, commit, rootState }) {
			const now = Date.now();
			if (now - state.lastRefresh < 2_000) {
				return;
			}
			state.lastRefresh = now;

			// Remove old transactions (older than 1 minute)
			commit('remove_old_transactions');

			const sigs = [];
			const keys = Object.keys(state.activeSignatures);
			for (let i = 0; i < keys.length; i++) {
				if (keys[i].length < 32) continue; //Pending sigs

				const si = state.activeSignatures[keys[i]];
				// Skip signatures that already have a result (confirmed, finalized, timeout, or error)
				if (
					si?.confirmationStatus === 'confirmed' ||
					si?.confirmationStatus === 'finalized' ||
					si?.confirmationStatus === 'timeout' ||
					si?.err
				) {
					continue;
				}

				// Skip signatures that have confirmations > 0
				if (si?.confirmations > 0) continue;

				sigs.push(keys[i]);
			}
			if (!sigs.length) return;

			// console.log("signatures::refresh fetching:", sigs, sigs.length)
			// First try without searchTransactionHistory (faster, recent txns only)
			let status = await rootState.solana.client
				.getSignatureStatuses(sigs, { searchTransactionHistory: false })
				.catch((err) => {
					console.error('Error fetching signature statuses:', err);
					return null;
				});

			// If we got null results and signatures are older than 10 seconds, try with searchTransactionHistory
			if (!status || (status.value && status.value.every((s) => s === null))) {
				const oldestSig = sigs.reduce((oldest, sig) => {
					const age = new Date() - state.activeSignatures[sig]?.createdAt;
					return Math.max(oldest, age);
				}, 0);

				if (oldestSig > 10_000) {
					console.log(
						'Retrying getSignatureStatuses with searchTransactionHistory=true for',
						sigs.length,
						'signatures'
					);
					status = await rootState.solana.client
						.getSignatureStatuses(sigs, { searchTransactionHistory: true })
						.catch((err) => {
							console.error('Error fetching signature statuses (with history):', err);

							// Mark transactions as error state manually when the API call fails
							sigs.forEach((sig) => {
								if (state.activeSignatures[sig]) {
									// Set generic error for transactions that failed
									commit('update_status', {
										sig,
										status: {
											confirmationStatus: 'error',
											err: 'Error',
										},
									});
									state.signatureStatus.postMessage({
										sig,
										status: {
											confirmationStatus: 'error',
											err: 'Error',
										},
									});
								}
							});

							return null;
						});
				}
			}

			if (Object.keys(state.activeSignatures).length === 0) return; //Cleared between the 2 checks

			// Guard: if status is null or undefined, skip processing
			if (!status || !status.value) {
				console.warn('getSignatureStatuses returned null/undefined, skipping status update');
				return;
			}

			status.value.forEach((s, i) => {
				const age = new Date() - state.activeSignatures[sigs[i]]?.createdAt;
				// console.log(`Signature ${sigs[i]} age: ${age}ms`, JSON.stringify(state.activeSignatures[sigs[i]])); // Add logging

				// Preserve token information when updating status
				const tokenInfo = state.activeSignatures[sigs[i]]?.tokenInfo;

				if (
					state.activeSignatures[sigs[i]]?.confirmationStatus === 'confirmed' ||
					state.activeSignatures[sigs[i]]?.confirmationStatus === 'finalized' ||
					state.activeSignatures[sigs[i]]?.confirmationStatus === 'timeout' ||
					state.activeSignatures[sigs[i]]?.err
				) {
					commit('update_status', { sig: sigs[i], status: s, tokenInfo });
					state.signatureStatus.postMessage({ sig: sigs[i], status: s, tokenInfo });
					return; //Skip only if we have already set to finalized or already errored
				}

				if (new Date() - state.activeSignatures[sigs[i]]?.createdAt > 60_000) {
					commit('update_status', {
						sig: sigs[i],
						status: { confirmationStatus: 'timeout', err: 'Timeout' },
						tokenInfo,
					});
					state.signatureStatus.postMessage({
						sig: sigs[i],
						status: { confirmationStatus: 'timeout', err: 'Timeout' },
						tokenInfo,
					});
					return; //Skip only if we have already set to finalized or already errored
				}

				// Handle null status from API - signature might not be found yet
				if (!s) {
					console.warn(
						`getSignatureStatuses returned null for signature ${sigs[i]}, age: ${age}ms`
					);
					// Don't update status if null, keep current status
					// Only timeout if signature has been pending for more than 30 seconds
					if (age > 30_000) {
						console.warn(`Signature ${sigs[i]} not found after ${age}ms, marking as timeout`);
						commit('update_status', {
							sig: sigs[i],
							status: { confirmationStatus: 'timeout', err: 'Transaction not found' },
							tokenInfo,
						});
						state.signatureStatus.postMessage({
							sig: sigs[i],
							status: { confirmationStatus: 'timeout', err: 'Transaction not found' },
							tokenInfo,
						});
					}
					return;
				}

				commit('update_status', { sig: sigs[i], status: s, tokenInfo });

				state.signatureStatus.postMessage({ sig: sigs[i], status: s, tokenInfo });
			});
		},
	},
};
